import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { STORAGE_KEYS } from "../constants/config";
import { offerService, stockService } from "../services/apiService";

const getRoles = () => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) return [];
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.roles || [];
  } catch { return []; }
};

const SlidingMenu = ({ mobileOpen = false, onCloseMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const [stockMenuOpen, setStockMenuOpen] = useState(false);
  const [distributorMenuOpen, setDistributorMenuOpen] = useState(false);
  const [distributorBillMenuOpen, setDistributorBillMenuOpen] = useState(false);
  const [invoiceMenuOpen, setInvoiceMenuOpen] = useState(false);
  const [masterMenuOpen, setMasterMenuOpen] = useState(false);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [offersMenuOpen, setOffersMenuOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Per-client Offers channel. Offers menu shows when != "off"; the
  // "Connect WhatsApp" submenu shows only when the channel is "whatsapp_api".
  const [offerChannel, setOfferChannel] = useState("off");
  const offersEnabled = offerChannel && offerChannel !== "off";
  const isWhatsAppApi = offerChannel === "whatsapp_api";

  const [gstReportEnabled, setGstReportEnabled] = useState(false);

  const refreshOfferChannel = useCallback(() => {
    if (!localStorage.getItem(STORAGE_KEYS.TOKEN)) {
      setOfferChannel("off");
      return;
    }
    offerService
      .getOfferChannel()
      .then((res) => setOfferChannel(res?.value || "off"))
      .catch(() => setOfferChannel("off"));
  }, []);

  const refreshGstReportEnabled = useCallback(() => {
    if (!localStorage.getItem(STORAGE_KEYS.TOKEN)) {
      setGstReportEnabled(false);
      return;
    }
    stockService
      .getGstReportEnabled()
      .then((res) => setGstReportEnabled(res?.value === true))
      .catch(() => setGstReportEnabled(false));
  }, []);

  // Re-fetch on mount, on every route change, and when an admin saves the
  // channel (custom event) or another tab updates it (storage/focus) — so the
  // menu reflects the change without a manual page refresh.
  useEffect(() => {
    refreshOfferChannel();
    refreshGstReportEnabled();
    window.addEventListener("offer-channel-changed", refreshOfferChannel);
    window.addEventListener("gst-report-changed", refreshGstReportEnabled);
    window.addEventListener("focus", refreshOfferChannel);
    return () => {
      window.removeEventListener("offer-channel-changed", refreshOfferChannel);
      window.removeEventListener("gst-report-changed", refreshGstReportEnabled);
      window.removeEventListener("focus", refreshOfferChannel);
    };
  }, [location, refreshOfferChannel, refreshGstReportEnabled]);

  const isAdmin = getRoles().some((r) => r.toLowerCase() === "admin");
  const isSuperAdmin = getRoles().some((r) => r.toLowerCase() === "super-admin-jash");

  const routes = [
    { 
      name: "Master", 
      icon: "fas fa-layer-group",
      subRoutes: [
        { name: "Product Category", path: "/master/product-category", icon: "fas fa-tags" },
        { name: "Product", path: "/master/product", icon: "fas fa-pills" },
        { name: "Print Labels", path: "/stock/print-labels", icon: "fas fa-barcode" },
        { name: "Settings", path: "/master/settings", icon: "fas fa-sliders-h" },
      ]
    },
    { 
      name: "Invoice", 
      icon: "fas fa-shopping-basket",
      subRoutes: [
        { name: "Search", path: "/invoice/search", icon: "fas fa-search" },
        { name: "Create", path: "/invoice", icon: "fas fa-plus-circle" },
      ]
    },
    {
      name: "Report",
      icon: "fas fa-chart-bar",
      subRoutes: [
        { name: "Sells", path: "/sells", icon: "fas fa-receipt" },
        { name: "Product Sales Report", path: "/report/product-sales", icon: "fas fa-chart-bar" },
        ...(gstReportEnabled
          ? [{ name: "GST Report", path: "/report/gst", icon: "fas fa-file-invoice-dollar" }]
          : []),
      ]
    },
    {
      name: "Customers",
      icon: "fas fa-users",
      subRoutes: [
        { name: "Search", path: "/customers", icon: "fas fa-search" },
        { name: "Returns", path: "/returns", icon: "fas fa-undo-alt" },
        { name: "Payments", path: "/payments/pending", icon: "fas fa-hand-holding-usd" },
      ]
    },
    { 
      name: "Stock", 
      icon: "fas fa-pills",
      subRoutes: [
        { name: "Search", path: "/stock/search", icon: "fas fa-search" },
        { name: "Create", path: "/stock/create", icon: "fas fa-plus-circle" },
      ]
    },
    { 
      name: "Distributor", 
      icon: "fas fa-truck",
      subRoutes: [
        { name: "Search", path: "/distributor/search", icon: "fas fa-search" },
        { name: "Create", path: "/distributor/create", icon: "fas fa-plus-circle" },
        { 
          name: "Bill",
          icon: "fas fa-file-invoice",
          subRoutes: [
            { name: "Search", path: "/distributor/bill/search", icon: "fas fa-search" },
            { name: "Create", path: "/distributor/bill/create", icon: "fas fa-plus-circle" },
          ]
        },
      ]
    },
    {
      name: "Offers",
      icon: "fas fa-bullhorn",
      offersOnly: true,
      subRoutes: [
        { name: "Search", path: "/offers/search", icon: "fas fa-search" },
        { name: "Create", path: "/offers/create", icon: "fas fa-plus-circle" },
        // Connect WhatsApp only when the client's channel is WhatsApp API.
        ...(isWhatsAppApi
          ? [{ name: "Connect WhatsApp", path: "/offers/whatsapp-connect", icon: "fab fa-whatsapp" }]
          : []),
      ]
    },
    { name: "Audit", path: "/audit", icon: "fas fa-chart-line", adminOnly: true },
    { name: "Admin Panel", path: "/admin", icon: "fas fa-cog", superAdminOnly: true },
  ].filter(route =>
      (!route.adminOnly || isAdmin) &&
      (!route.superAdminOnly || isSuperAdmin) &&
      (!route.offersOnly || offersEnabled)
    ).sort((a, b) => a.name.localeCompare(b.name));

  const filteredRoutes = routes.filter(route => {
    const matchesName = route.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSubRoute = route.subRoutes?.some(sub => 
      sub.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.subRoutes?.some(nested => nested.name.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    return matchesName || matchesSubRoute;
  });

  const showSidebar = isHovered || isPinned;

  return (
    <>
      {/* Mobile overlay backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={onCloseMobile} />
      )}
      {/* Desktop: normal sidebar */}
      <div className={`hidden md:block flex-shrink-0 transition-all duration-300 ${showSidebar ? 'w-64' : 'w-16'}`}>
        <div
          className={`fixed left-0 top-0 h-full bg-gradient-to-b from-blue-50 to-indigo-100 shadow-xl transition-all duration-300 z-40 ${
            showSidebar ? 'w-64' : 'w-16'
          }`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
        <div className="flex items-center justify-between p-4 border-b border-blue-200">
          {showSidebar ? (
            <>
              <input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={() => navigate('/home')}
                className={`ml-2 p-2 rounded-lg transition-colors ${
                  currentPath === '/home'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-200 text-blue-700 hover:bg-blue-300'
                }`}
                title="Go to Home"
              >
                <i className="fas fa-home"></i>
              </button>
              <button
                onClick={() => setIsPinned(!isPinned)}
                className={`ml-1 p-2 rounded-lg transition-colors ${
                  isPinned 
                    ? 'bg-blue-500 text-white hover:bg-blue-600' 
                    : 'bg-blue-200 text-blue-700 hover:bg-blue-300'
                }`}
                title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
              >
                <i className={`fas fa-thumbtack ${isPinned ? '' : 'rotate-45'}`}></i>
              </button>
            </>
          ) : (
            <i className="fas fa-search text-blue-700 text-xl mx-auto"></i>
          )}
        </div>

        <div className="overflow-y-auto h-[calc(100%-73px)] py-4">
          {filteredRoutes.map((route, index) => (
            <div key={route.path || route.name}>
              {route.subRoutes ? (
                <div>
                  <div
                    className={`flex items-center justify-between gap-2 px-4 py-3 cursor-pointer transition-all ${
                      (route.name === 'Stock' && currentPath.startsWith('/stock')) || 
                      (route.name === 'Distributor' && currentPath.startsWith('/distributor')) ||
                      (route.name === 'Invoice' && currentPath.startsWith('/invoice')) ||
                      (route.name === 'Master' && currentPath.startsWith('/master')) ||
                      (route.name === 'Customers' && (currentPath.startsWith('/customers') || currentPath.startsWith('/returns') || currentPath.startsWith('/payments'))) ||
                      (route.name === 'Report' && (currentPath === '/sells' || currentPath.startsWith('/report'))) ||
                      (route.name === 'Offers' && currentPath.startsWith('/offers'))
                        ? 'bg-blue-200 text-blue-900 font-semibold' 
                        : 'text-blue-700 hover:text-blue-900 hover:bg-blue-100'
                    }`}
                    onClick={() => {
                      if (route.name === 'Master') {
                        setMasterMenuOpen(!masterMenuOpen);
                        setStockMenuOpen(false);
                        setDistributorMenuOpen(false);
                        setInvoiceMenuOpen(false);
                      } else if (route.name === 'Stock') {
                        setStockMenuOpen(!stockMenuOpen);
                        setDistributorMenuOpen(false);
                        setDistributorBillMenuOpen(false);
                        setInvoiceMenuOpen(false);
                        setMasterMenuOpen(false);
                      } else if (route.name === 'Distributor') {
                        setDistributorMenuOpen(!distributorMenuOpen);
                        setStockMenuOpen(false);
                        setInvoiceMenuOpen(false);
                        setMasterMenuOpen(false);
                      } else if (route.name === 'Invoice') {
                        setInvoiceMenuOpen(!invoiceMenuOpen);
                        setStockMenuOpen(false);
                        setDistributorMenuOpen(false);
                        setMasterMenuOpen(false);
                        setCustomerMenuOpen(false);
                      } else if (route.name === 'Customers') {
                        setCustomerMenuOpen(!customerMenuOpen);
                        setStockMenuOpen(false);
                        setDistributorMenuOpen(false);
                        setInvoiceMenuOpen(false);
                        setMasterMenuOpen(false);
                      } else if (route.name === 'Report') {
                        setReportMenuOpen(!reportMenuOpen);
                        setStockMenuOpen(false);
                        setDistributorMenuOpen(false);
                        setInvoiceMenuOpen(false);
                        setMasterMenuOpen(false);
                        setCustomerMenuOpen(false);
                      } else if (route.name === 'Offers') {
                        setOffersMenuOpen(!offersMenuOpen);
                        setStockMenuOpen(false);
                        setDistributorMenuOpen(false);
                        setInvoiceMenuOpen(false);
                        setMasterMenuOpen(false);
                        setCustomerMenuOpen(false);
                        setReportMenuOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <i className={`${route.icon} text-lg w-6`}></i>
                      {showSidebar && <span className="text-sm font-medium">{route.name}</span>}
                    </div>
                    {showSidebar && (
                      <i className={`fas fa-chevron-${
                        (route.name === 'Stock' && stockMenuOpen) || 
                        (route.name === 'Distributor' && distributorMenuOpen) ||
                        (route.name === 'Invoice' && invoiceMenuOpen) ||
                        (route.name === 'Master' && masterMenuOpen) ||
                        (route.name === 'Customers' && customerMenuOpen) ||
                        (route.name === 'Report' && reportMenuOpen) ||
                        (route.name === 'Offers' && offersMenuOpen)
                          ? 'up' : 'down'
                      } text-xs`}></i>
                    )}
                  </div>
                  
                  {((route.name === 'Stock' && stockMenuOpen) || 
                    (route.name === 'Distributor' && distributorMenuOpen) ||
                    (route.name === 'Invoice' && invoiceMenuOpen) ||
                    (route.name === 'Master' && masterMenuOpen) ||
                    (route.name === 'Customers' && customerMenuOpen) ||
                    (route.name === 'Report' && reportMenuOpen) ||
                    (route.name === 'Offers' && offersMenuOpen)) && showSidebar && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 ml-4">
                      {route.subRoutes.map((subRoute) => (
                        subRoute.subRoutes ? (
                          <div key={subRoute.name}>
                            <div
                              className={`flex items-center justify-between gap-3 px-4 py-2 cursor-pointer transition-all ${
                                currentPath.startsWith('/distributor/bill')
                                  ? 'bg-blue-100 text-blue-900 font-semibold'
                                  : 'text-blue-600 hover:text-blue-900 hover:bg-blue-100'
                              }`}
                              onClick={() => setDistributorBillMenuOpen(!distributorBillMenuOpen)}
                            >
                              <div className="flex items-center gap-3">
                                <i className={`${subRoute.icon} text-sm w-6`}></i>
                                <span className="text-sm">{subRoute.name}</span>
                              </div>
                              <i className={`fas fa-chevron-${distributorBillMenuOpen ? 'up' : 'down'} text-xs`}></i>
                            </div>
                            {distributorBillMenuOpen && (
                              <div className="bg-blue-100 border-l-4 border-blue-500 ml-4">
                                {subRoute.subRoutes.map((nestedRoute) => (
                                  <Link
                                    key={nestedRoute.path}
                                    to={nestedRoute.path}
                                    className={`flex items-center gap-3 px-4 py-2 transition-all ${
                                      currentPath === nestedRoute.path
                                        ? 'bg-blue-300 text-blue-900 font-semibold'
                                        : 'text-blue-700 hover:text-blue-900 hover:bg-blue-200'
                                    }`}
                                  >
                                    <i className={`${nestedRoute.icon} text-xs w-6`}></i>
                                    <span className="text-xs">{nestedRoute.name}</span>
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            key={subRoute.path}
                            to={subRoute.path}
                            className={`flex items-center gap-3 px-4 py-2 transition-all ${
                              currentPath === subRoute.path
                                ? 'bg-blue-200 text-blue-900 font-semibold'
                                : 'text-blue-600 hover:text-blue-900 hover:bg-blue-100'
                            }`}
                          >
                            <i className={`${subRoute.icon} text-sm w-6`}></i>
                            <span className="text-sm">{subRoute.name}</span>
                          </Link>
                        )
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={route.path}
                  className={`flex items-center gap-3 px-4 py-3 transition-all ${
                    currentPath === route.path
                      ? 'bg-blue-200 text-blue-900 font-semibold'
                      : 'text-blue-700 hover:text-blue-900 hover:bg-blue-100'
                  }`}
                >
                  <i className={`${route.icon} text-lg w-6`}></i>
                  {showSidebar && <span className="text-sm font-medium">{route.name}</span>}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
    {/* Mobile: slide-in overlay menu */}
    <div className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-blue-50 to-indigo-100 shadow-xl z-50 transform transition-transform duration-300 md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between p-4 border-b border-blue-200">
        <span className="text-sm font-bold text-blue-800">Menu</span>
        <button onClick={onCloseMobile} className="p-2 rounded-lg bg-blue-200 text-blue-700 hover:bg-blue-300">
          <i className="fas fa-times"></i>
        </button>
      </div>
      <div className="overflow-y-auto h-[calc(100%-73px)] py-4">
        {filteredRoutes.map((route, index) => (
          <div key={(route.path || route.name) + '-mobile'}>
            {route.subRoutes ? (
              <div>
                <div
                  className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                  onClick={() => {
                    if (route.name === 'Master') setMasterMenuOpen(!masterMenuOpen);
                    else if (route.name === 'Stock') setStockMenuOpen(!stockMenuOpen);
                    else if (route.name === 'Distributor') setDistributorMenuOpen(!distributorMenuOpen);
                    else if (route.name === 'Invoice') setInvoiceMenuOpen(!invoiceMenuOpen);
                    else if (route.name === 'Customers') setCustomerMenuOpen(!customerMenuOpen);
                    else if (route.name === 'Report') setReportMenuOpen(!reportMenuOpen);
                    else if (route.name === 'Offers') setOffersMenuOpen(!offersMenuOpen);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <i className={`${route.icon} text-lg w-6`}></i>
                    <span className="text-sm font-medium">{route.name}</span>
                  </div>
                  <i className={`fas fa-chevron-${
                    (route.name === 'Stock' && stockMenuOpen) ||
                    (route.name === 'Distributor' && distributorMenuOpen) ||
                    (route.name === 'Invoice' && invoiceMenuOpen) ||
                    (route.name === 'Master' && masterMenuOpen) ||
                    (route.name === 'Customers' && customerMenuOpen) ||
                    (route.name === 'Report' && reportMenuOpen) ||
                    (route.name === 'Offers' && offersMenuOpen)
                      ? 'up' : 'down'
                  } text-xs`}></i>
                </div>
                {((route.name === 'Stock' && stockMenuOpen) ||
                  (route.name === 'Distributor' && distributorMenuOpen) ||
                  (route.name === 'Invoice' && invoiceMenuOpen) ||
                  (route.name === 'Master' && masterMenuOpen) ||
                  (route.name === 'Customers' && customerMenuOpen) ||
                  (route.name === 'Report' && reportMenuOpen) ||
                  (route.name === 'Offers' && offersMenuOpen)) && (
                  <div className="ml-6 border-l-2 border-blue-200">
                    {route.subRoutes.map((sub) => (
                      sub.subRoutes ? (
                        <div key={sub.path || sub.name}>
                          <div className="flex items-center gap-2 px-4 py-2 text-blue-600 cursor-pointer hover:bg-blue-50"
                            onClick={() => { if (sub.name === 'Bill') setDistributorBillMenuOpen(!distributorBillMenuOpen); }}>
                            <i className={`${sub.icon} text-sm`}></i>
                            <span className="text-xs">{sub.name}</span>
                            <i className={`fas fa-chevron-${distributorBillMenuOpen ? 'up' : 'down'} text-xs ml-auto`}></i>
                          </div>
                          {distributorBillMenuOpen && (
                            <div className="ml-4">
                              {sub.subRoutes.map((nested) => (
                                <Link key={nested.path} to={nested.path} onClick={onCloseMobile}
                                  className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 text-xs">
                                  <i className={`${nested.icon} text-xs`}></i>
                                  <span>{nested.name}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Link key={sub.path} to={sub.path} onClick={onCloseMobile}
                          className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
                            currentPath === sub.path ? 'bg-blue-200 text-blue-900 font-semibold' : 'text-blue-600 hover:bg-blue-50'
                          }`}>
                          <i className={`${sub.icon} text-sm`}></i>
                          <span className="text-xs">{sub.name}</span>
                        </Link>
                      )
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link to={route.path} onClick={onCloseMobile}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  currentPath === route.path ? 'bg-blue-200 text-blue-900 font-semibold' : 'text-blue-700 hover:text-blue-900 hover:bg-blue-100'
                }`}>
                <i className={`${route.icon} text-lg w-6`}></i>
                <span className="text-sm font-medium">{route.name}</span>
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
    </>
  );
};

export default SlidingMenu;
